import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import {
  getAllAccessLLMModels,
  getCategories,
  getTemplatesAdmin,
  getHelpPageDocuments,
} from "api";
import type { RootState } from "../../../store";

export const useAdminCounts = (): Record<string, number | undefined> => {
  const users = useSelector((state: RootState) => state.usersSlice.value);
  const usersLoading = useSelector(
    (state: RootState) => state.usersSlice.isLoading,
  );
  const announcements = useSelector(
    (state: RootState) => state.announcementsSlice.value,
  );
  const announcementsLoading = useSelector(
    (state: RootState) => state.announcementsSlice.isLoading,
  );
  const { data: aiModels, isLoading: aiModelsLoading } = useQuery(
    "allAccessLLMModels",
    getAllAccessLLMModels,
    { refetchOnWindowFocus: false, staleTime: 0 },
  );
  const useCases = useSelector((state: RootState) => state.useCasesSlice.value);
  const useCasesLoading = useSelector(
    (state: RootState) => state.useCasesSlice.isLoading,
  );
  const useCasePrompts = useSelector(
    (state: RootState) => state.useCasePromptsSlice.value,
  );
  const useCasePromptsLoading = useSelector(
    (state: RootState) => state.useCasePromptsSlice.isLoading,
  );
  const workspaces = useSelector(
    (state: RootState) => state.workspacesSlice.value,
  );
  const workspacesLoading = useSelector(
    (state: RootState) => state.workspacesSlice.isLoading,
  );

  const { data: categories } = useQuery(
    "categories",
    () => getCategories(false),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      staleTime: 0,
      cacheTime: 5 * 60 * 1000,
    },
  );

  const { data: templatesResponse } = useQuery(
    "templates-admin",
    getTemplatesAdmin,
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  );

  const { data: helpDocs } = useQuery(
    "help-page-documents",
    getHelpPageDocuments,
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  );

  const templates = (templatesResponse as any)?.templates;
  const tags = categories?.flatMap((c: any) => c.tags ?? []);

  return {
    tags: tags ? tags.length : undefined,
    categories: categories ? categories.length : undefined,
    useCases: useCasesLoading ? undefined : useCases.length,
    useCasePrompts: useCasePromptsLoading ? undefined : useCasePrompts.length,
    workspaces: workspacesLoading ? undefined : workspaces.length,
    users: usersLoading ? undefined : users.length,
    announcements: announcementsLoading ? undefined : announcements.length,
    aiAccess: aiModelsLoading ? undefined : (aiModels ?? []).length,
    promptSuggestions: useCasesLoading
      ? undefined
      : useCases.flatMap((uc: any) => uc.promptSuggestions ?? []).length,
    templates: templates ? templates.length : undefined,
    helpPageDocuments: helpDocs ? (helpDocs as any[]).length : undefined,
    feedback: undefined,
  };
};
